import { Box, Grid2, TextField, Typography } from "@mui/material";
import { format, addMonths, startOfMonth, endOfMonth } from "date-fns";
import { MaterialReactTable, MRT_ColumnDef, MRT_Row } from "material-react-table";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useMemo } from "react";
import { DashboardContainer } from "src/layouts/components/content";
import toast from "react-hot-toast";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { Iconify } from 'src/components/iconify';
import DailogPopup from 'src/layouts/components/dailog';
import { Controller, useForm } from 'react-hook-form';
import { MobileTimePicker, TimePicker } from '@mui/x-date-pickers';
import { memo } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { createServiceLog, getAllLogs } from 'src/services/super_admin/superAdminService';
import { Filter } from "bad-words";
import { createPatientLog, getPatientComplaintById } from "src/services/patient/patientService";
import { usePatient } from "src/store/userContext";

const filter = new Filter();

const PatientQuery = observer(() => {
    const [serviceLogs, setServiceLogs] = useState<ComplaintLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false); // 👈 NEW

    const [isLogFormOpen, setIsLogFormOpen] = useState(false);
    const patient = usePatient();
    const patientId = patient?.selectedPatient?.id;


    const handleOpenLogForm = () => setIsLogFormOpen(true);
    const handleCloseLogForm = () => setIsLogFormOpen(false);

    const handleSubmitLog = async (data: any) => {
        if (!patientId) {
            toast.error("No patient selected");
            return;
        }
         setIsSubmitting(true);
        try {
            const selectedDate = new Date(data.date);
            selectedDate.setHours(0, 0, 0, 0);
            const serviceDateTimestamp = Math.floor(selectedDate.getTime() / 1000);

            const fullDateWithTime = new Date(data.date);
            fullDateWithTime.setSeconds(data.time);
            const serviceTimeTimestamp = Math.floor(fullDateWithTime.getTime() / 1000);

            const payload = {
                patientId: patient?.patients[0].id,
                serviceType: data.serviceType,
                complaintDate: serviceDateTimestamp,
                complaintTime: serviceTimeTimestamp,
                remark: data.remark,
                createdDate: Math.floor(Date.now() / 1000),
            };

            await createPatientLog(payload);

            const response = await getPatientComplaintById(patient?.patients[0].id);
            if (response?.data) {
                const logs = response.data.map((item: any) => new ComplaintLog(item));
                setServiceLogs(logs);
            }

            const now = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(now.getDate() - 30);

            setFromDate(thirtyDaysAgo);
            setToDate(now);

            toast.success("Thank you for reaching out. We take your concerns seriously.");
            setIsLogFormOpen(false);
        } catch (error) {
            toast.error("Failed to add service log");
        }
         finally {
        setIsSubmitting(false); // 👈 STOP loading
    }
    };


    const currentDate = new Date();
    const thirtyDaysAgo = new Date(currentDate);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const [fromDate, setFromDate] = useState<Date | null>(thirtyDaysAgo);
    const [toDate, setToDate] = useState<Date | null>(currentDate);



useEffect(() => {
  if (!patient?.selectedPatient?.id) return;

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
     const response = await getPatientComplaintById(patient?.patients[0].id); 
      const logs = response?.data?.map((item: any) => new ComplaintLog(item)) || [];
      setServiceLogs(logs);
    } catch {
      toast.error("Failed to fetch service logs");
    } finally {
      setIsLoading(false);
    }
  };

  fetchLogs();
}, [patient?.selectedPatient?.id]);


    const columns = useMemo<MRT_ColumnDef<ComplaintLog>[]>(() => [
        {
            accessorKey: 'serviceType',
            header: 'Service Type',
            size: 150,
        },
        // {
        //     accessorKey: 'serviceDate',
        //     header: 'Date',
        //     size: 150,
        //     Cell: ({ row }: { row: MRT_Row<ComplaintLog> }) => {
        //         const date = row.original.complaintDate;
        //         return date ? format(new Date(date * 1000), 'dd/MM/yyyy') : '';
        //     },
        // },

         {
              id: 'complaintDate',
              header: '  Date',
              accessorFn: (row) =>
                row.complaintDate ? format(new Date(row.complaintDate*1000), 'dd/MM/yyyy') : '',
            },
          {
             id: 'time',
             header: 'Time',
             accessorFn: (row) =>
               row.complaintTime ? format(new Date(row.complaintTime*1000),  'hh:mm a') : '',
           },


        {
            accessorKey: 'remark',
            header: 'Remark',
            size: 250,

        },
    ], []);




    const handleFromDateChange = (newValue: Date | null) => {
        setFromDate(newValue);
        if (newValue && toDate && newValue > toDate) {
            setToDate(null);
        }
    };

    const handleToDateChange = (newValue: Date | null) => {
        setToDate(newValue);
        if (newValue && fromDate && newValue < fromDate) {
            setFromDate(null);
        }
    };
const filteredData = useMemo(() => {
    return serviceLogs.filter(item => {
        if (!item.complaintDate) return false;
        const itemDate = new Date(item.complaintDate * 1000);

        if (isNaN(itemDate.getTime())) return false;

        // Strip time, only compare date
        const itemYMD = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
        const fromYMD = fromDate ? new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate()) : null;
        const toYMD = toDate ? new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate()) : null;

        const matchesFromDate = fromYMD ? itemYMD >= fromYMD : true;
        const matchesToDate = toYMD ? itemYMD <= toYMD : true;

        return matchesFromDate && matchesToDate;
    });
}, [serviceLogs, fromDate, toDate]);




    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} p={2}>
            <DashboardContainer
                icon="fluent-color:notebook-question-mark-20"
                title="Complaint logging"
                isbutton={true}
                buttonProps={{
                    buttonTitle: 'Raise Query',
                    buttonIcon: <Iconify icon={'raphael:question2'} />,
                    onbuttonClick: handleOpenLogForm,
                }}

            >
                <Box mb={3}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, sm: 2 }}>
                            <MobileDatePicker
                                label="From Date"
                                maxDate={toDate ?? undefined}
                                value={fromDate}
                                onChange={handleFromDateChange}
                                format="dd/MM/yyyy"
                                disableFuture
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 2 }}>
                            <MobileDatePicker
                                label="To Date"
                                maxDate={new Date()}
                                minDate={fromDate ?? undefined}
                                value={toDate}
                                onChange={handleToDateChange}
                                disableFuture
                                format="dd/MM/yyyy"
                            />
                        </Grid2>
                    </Grid2>
                </Box>

                <MaterialReactTable
                    columns={columns}
                    data={filteredData}
                    muiTableHeadRowProps={{ sx: { bgcolor: 'primary.light', color: 'white' } }}
                    muiTableHeadCellProps={{ sx: { color: 'black' } }}
                    muiTableBodyRowProps={{ sx: { bgcolor: 'white' } }}
                    muiTableBodyCellProps={{ sx: { borderBottom: '1px solid #ccc' } }}
                    enableTopToolbar={false}
                    enableEditing={false}
                    enableSorting={false}
                />

            </DashboardContainer>
            <Form
                open={isLogFormOpen}
                onClose={handleCloseLogForm}
                onSubmit={handleSubmitLog}
                 loading={isSubmitting}
            />
        </Box>
    );
});

export class ComplaintLog {
    patientName: string;
    patientId: number;
    complaintDate: number;
    complaintTime?: number;
    serviceType?: string;
    remark?: string;
    createdDate?: number;
    isPatient?: string;


    constructor(data: any) {
        this.patientName = data.patientName;
        this.patientId = data.patientId;
        this.complaintDate = data.complaintDate;
        this.complaintTime = data.complaintTime;
        this.serviceType = data.serviceType;
        this.remark = data.remark;
        this.createdDate = data.createdDate;
    }
}

export const Form = memo(({ open, onClose, onSubmit,loading = false }: {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    loading?: boolean;
}) => {
    const partnerSchema = yup.object().shape({
        serviceType: yup.string().required("Service type is required").test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
        date: yup.string().required("Date is required"),
        time: yup.string().required("Time is required"),
        remark: yup.string().required("Remark is required").test('is-not-profane', 'Profanity is not allowed', value => !filter.isProfane(value || '')),
    });

  const RequiredLabel = (label: string) => (
  <span>
    {label} <span style={{ color: 'red' }}>*</span>
  </span>
);

    const { control, handleSubmit, reset } = useForm({
        resolver: yupResolver(partnerSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            serviceType: "",
            date: "",
            time: "",
            remark: "",
        },
    });

    useEffect(() => {
        if (open) reset();
    }, [open, reset]);

    return (
        <DailogPopup open={open} onSubmit={handleSubmit(onSubmit)} onCancel={onClose} loading={loading}>
            <Box maxWidth={600} mx="auto" px={2}>
                <Grid2 container spacing={2} justifyContent="center">
                    <Grid2 size={{ xs: 12, sm: 8 }} container spacing={2}>
                        <Controller
                            name="serviceType"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label={RequiredLabel("What service did you take? (Enter NA if this is a query)")}
                                    size="small"
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }} container spacing={2}>
                        <Controller
                            name="date"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <MobileDatePicker
                                    label={RequiredLabel("Date")}
                                    value={field.value}
                                    onChange={field.onChange}
                                    disableFuture
                                    format="dd/MM/yyyy"
                                    slotProps={{
                                        textField: { fullWidth: true, size: "small", error: !!error, helperText: error?.message }
                                    }}
                                />
                            )}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }}>
                        <Controller
                            name="time"
                            control={control}
                            render={({ field, fieldState: { error } }) => {
                                const getTimeAsDate = (seconds: number) => {
                                    const date = new Date();
                                    date.setHours(0, 0, 0, 0);
                                    date.setSeconds(seconds);
                                    return date;
                                };

                                return (
                                    <MobileTimePicker
                                        label={RequiredLabel("Time")}
                                        onChange={(newValue) => {
                                            if (newValue instanceof Date && !isNaN(newValue.getTime())) {
                                                const hours = newValue.getHours();
                                                const minutes = newValue.getMinutes();
                                                const seconds = (hours * 3600) + (minutes * 60);
                                                field.onChange(seconds);
                                            } else {
                                                field.onChange(null);
                                            }
                                        }}
                                        value={
                                            typeof field.value === 'number' ? getTimeAsDate(field.value) : null
                                        }
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: 'small',
                                                error: !!error,
                                                helperText: error?.message,
                                            },
                                        }}
                                    />
                                );
                            }}
                        />


                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }}>
                        <Controller
                            name="remark"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    size="small"
                                    label={RequiredLabel("Remark")}
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                    </Grid2>
                    <Typography variant="body2" color="textSecondary">Note:Fields marked with an asterisk (*) are required.</Typography>
                </Grid2>
            </Box>
        </DailogPopup>
    );
});

export default PatientQuery;